import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../api";

interface AuthContextValue {
  authenticated: boolean;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Ślad "byłem zalogowany" (Faza 7).
 *
 * Offline `GET /api/auth/me` nie odpowiada wcale - bez tej pamiątki appka
 * otwarta bez sieci pokazywałaby ekran logowania, a więc odcinałaby capture
 * offline. Ciasteczko `auth_token` jest httponly, więc flagi nie da się
 * podrobić na coś więcej niż "pokaż shell" - każde żądanie i tak weryfikuje
 * backend.
 */
const AUTHED_FLAG = "gtd.authenticated";

function rememberAuth(authenticated: boolean): void {
  try {
    if (authenticated) localStorage.setItem(AUTHED_FLAG, "1");
    else localStorage.removeItem(AUTHED_FLAG);
  } catch {
    /* prywatny tryb przeglądarki - trudno */
  }
}

function wasAuthenticated(): boolean {
  try {
    return localStorage.getItem(AUTHED_FLAG) === "1";
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        const me = await api.me();
        rememberAuth(me.authenticated);
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          rememberAuth(false);
          return { authenticated: false };
        }
        // Brak sieci: ufamy poprzedniemu logowaniu, żeby offline capture działał.
        if (!(err instanceof ApiError)) return { authenticated: wasAuthenticated() };
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({ mutationFn: api.login });

  const login = useCallback(
    async (token: string) => {
      await loginMutation.mutateAsync(token);
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    [loginMutation, queryClient],
  );

  const logout = useCallback(async () => {
    rememberAuth(false);
    await api.logout();
    queryClient.clear();
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      authenticated: data?.authenticated ?? false,
      loading: isLoading,
      login,
      logout,
    }),
    [data, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
